import { CAT } from '@/shared/CAT';

interface CanonicalResult {
  categories: Record<string, Array<{label: string; value: any; keyPath: string}>>;
  meters: any[];
  identifiers: any[];
  lineItems: any[];
  totalFields: number;
}

export function getNormalizedPayloadForExport(
  canonicalResult: CanonicalResult, 
  rawData: any,
  userDefinedFields: Array<{id: string; name: string; value: string}> = []
) {
  // Extract bill data from categories
  const bill = extractBillData(canonicalResult);
  const identifiers = extractIdentifierData(canonicalResult);
  const meters = extractMeterData(canonicalResult);
  const lineItems = extractLineItemData(canonicalResult);
  const userDefinedFieldsObj = extractCustomFields(userDefinedFields);

  return {
    bill,
    identifiers,
    meters,
    lineItems,
    userDefinedFields: userDefinedFieldsObj,
    metadata: {
      totalFields: canonicalResult.totalFields,
      exportedAt: new Date().toISOString(),
      source: 'parser-sandbox'
    }
  };
}

function extractBillData(canonicalResult: CanonicalResult): Record<string, any> {
  const basic = canonicalResult.categories[CAT.Basic] || [];
  const financial = canonicalResult.categories[CAT.Financial] || [];
  const address = canonicalResult.categories[CAT.Address] || [];
  const usage = canonicalResult.categories[CAT.Usage] || [];
  
  const data: Record<string, any> = {};
  
  // Map basic fields
  basic.forEach(field => {
    const key = field.label.toLowerCase().replace(/\s+/g, '_');
    data[key] = field.value;
  });
  
  // Map financial fields
  financial.forEach(field => {
    const key = field.label.toLowerCase().replace(/\s+/g, '_');
    data[key] = parseNumericValue(field.value);
  });
  
  // Map address fields
  address.forEach(field => {
    const key = field.label.toLowerCase().replace(/\s+/g, '_');
    data[key] = field.value;
  });
  
  // Map usage fields
  usage.forEach(field => {
    const key = field.label.toLowerCase().replace(/\s+/g, '_');
    data[key] = parseNumericValue(field.value);
  });
  
  return data;
}

function extractIdentifierData(canonicalResult: CanonicalResult): Record<string, any>[] {
  const identifierFields = canonicalResult.categories[CAT.Identifier] || [];
  
  // Group by identifier (simplified for now)
  const identifierData: Record<string, any> = {};
  
  identifierFields.forEach(field => {
    const key = field.label.toLowerCase().replace(/\s+/g, '_');
    identifierData[key] = parseNumericValue(field.value);
  });
  
  return [identifierData];
}

function extractMeterData(canonicalResult: CanonicalResult): Record<string, any>[] {
  const meterFields = canonicalResult.categories[CAT.Meter] || [];
  
  // Group meter fields by meter number (simplified for now)
  const meterData: Record<string, any> = {};
  
  meterFields.forEach(field => {
    const key = field.label.toLowerCase().replace(/\s+/g, '_');
    meterData[key] = parseNumericValue(field.value);
  });
  
  return [meterData];
}

function extractLineItemData(canonicalResult: CanonicalResult): Record<string, any>[] {
  const lineItemFields = canonicalResult.categories[CAT.LineItems] || [];
  
  // Group line item fields (simplified for now)
  const lineItemData: Record<string, any> = {};
  
  lineItemFields.forEach(field => {
    const key = field.label.toLowerCase().replace(/\s+/g, '_');
    lineItemData[key] = parseNumericValue(field.value);
  });
  
  return [lineItemData];
}

function extractCustomFields(userDefinedFields: Array<{id: string; name: string; value: string}>): Record<string, any> {
  const data: Record<string, any> = {};
  
  userDefinedFields.forEach(field => {
    if (field.name && field.value !== undefined) {
      data[field.name] = field.value;
    }
  });
  
  return data;
}

function parseNumericValue(value: any): any {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const num = parseFloat(value);
    return isNaN(num) ? value : num;
  }
  return value;
}
